import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

export const SolutionLayout = ({ eyebrow, title, lead, capabilities, steps, ctaText, testid }) => (
  <div data-testid={testid} className="bg-[#F5F4F0] text-[#121413] min-h-screen">
    <header className="border-b border-[#E2DFD7]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid={`${testid}-logo`} className="flex items-center group">
          <img
            src="/assets/lockup-side-mono-ink.png"
            alt="Shikra Energy"
            className="h-9 w-auto group-hover:opacity-70 transition-opacity duration-300"
          />
        </Link>
        <div className="flex items-center gap-6">
          <Link
            to="/"
            data-testid={`${testid}-back-link`}
            className="text-sm text-[#4A4D4A] hover:text-[#121413] transition-colors duration-300"
          >
            ← Back to overview
          </Link>
          <img src="/assets/iitm-emblem.png" alt="IIT Madras emblem" className="h-8 w-auto" />
        </div>
      </div>
    </header>

    <main>
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C4A3A] font-semibold">
              {eyebrow}
            </p>
            <h1
              data-testid={`${testid}-heading`}
              className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1] text-[#121413] max-w-3xl"
            >
              {title}
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#4A4D4A]">
              {lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#E2DFD7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <article
                data-testid={`${testid}-capability-${i + 1}`}
                className="grid grid-cols-12 gap-6 py-8 sm:py-10 border-b border-[#E2DFD7] group"
              >
                <p className="col-span-2 sm:col-span-1 font-mono text-2xl sm:text-3xl font-light text-[#C5C1B6] group-hover:text-[#2C4A3A] transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <div className="col-span-10 sm:col-span-11 lg:col-span-8 transition-transform duration-500 group-hover:translate-x-2">
                  <h2 className="text-lg sm:text-xl font-medium tracking-tight text-[#121413]">
                    {c.title}
                  </h2>
                  <p className="mt-2.5 max-w-2xl text-base leading-relaxed text-[#4A4D4A]">
                    {c.description}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EFECE6]/50 border-t border-[#E2DFD7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C4A3A] font-semibold">
              How deployment works
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-px border border-[#E2DFD7] bg-[#E2DFD7]">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="bg-[#FAF9F6]">
                <div
                  data-testid={`${testid}-step-${i + 1}`}
                  className="p-7 sm:p-8 h-full hover:bg-[#E8EFEB]/40 transition-colors duration-500"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B86A28]">
                    Step {String(i + 1).padStart(2, "0")} / {s.label}
                  </p>
                  <h3 className="mt-4 text-lg font-medium tracking-tight text-[#121413]">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#4A4D4A]">{s.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 border-t border-[#E2DFD7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-8">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#121413]">
              {ctaText}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              data-testid={`${testid}-cta`}
              to="/"
              state={{ scrollTo: "#contact" }}
              className="inline-flex items-center gap-3 bg-[#2C4A3A] text-[#FAF9F6] px-8 py-4 text-sm font-medium hover:bg-[#121413] transition-colors duration-300 group"
            >
              Get in touch
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>

    <footer className="border-t border-[#E2DFD7]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between">
        <img src="/assets/lockup-side-mono-ink.png" alt="Shikra Energy" className="h-8 w-auto" />
        <p className="text-xs text-[#7A7E7A]">© 2026 Shikra Energy. All rights reserved.</p>
      </div>
    </footer>
  </div>
);
